/* ── Chat in a guided tour room ────────────────────────────────────────
   The rules of this file are set by two things: where the messages
   travel, and what a buyer says in them.

   ── WHY NOT THE SYNC RELAY ──
   utils/guidedRoomRelay.ts carries the raised hand over `wss://vrsync…`,
   and says in as many words that chat must not follow it: that relay
   authenticates nobody, so anyone holding a room id can read everything
   in it. A raised hand is a boolean worth nothing to an eavesdropper.
   What a buyer types about their budget, their loan, or which unit their
   parents prefer is not. That constraint hasn't changed.

   ── WHY AGORA'S DATA STREAM ──
   The room already holds an authenticated Agora channel for voice: the
   channel comes from the room, the uid and publish rights come from the
   backend's presence record, and the token is signed for that exact uid
   (see hooks/useGuidedRoomVoice.ts). Anyone in a position to read this
   stream is already in a position to hear the tour. So chat rides the
   client that is already joined, and inherits an authorisation model
   that exists today rather than waiting for `ws_token`, whose signing
   key the backend still hasn't set.

   ── WHAT THIS COSTS ──
   Nothing is stored. A data stream reaches whoever is in the channel at
   that moment: no history, no delivery to someone who joins later, and
   nothing readable after the room ends. That is a real limitation and
   the UI says so rather than implying a transcript exists. When the
   backend grows a message store, `sendChat` and the receive handler are
   the only two places that change.

   ── THE 1 KB WALL ──
   sendStreamMessage throws "stream message out of range" above 1024
   BYTES — measured on the encoded payload, not the string length. This
   matters more than it looks: 600 characters of English is ~600 bytes,
   but 600 characters of Hindi or Kannada is closer to 1800, and a family
   on a tour in Bengaluru is exactly who would find that edge. So every
   cap in this file is a byte cap, and a frame is measured with the text
   already in it rather than assumed to fit.

   ── THE SDK DOESN'T ADMIT THIS API EXISTS ──
   agora-rtc-sdk-ng 4.18.2 ships `sendStreamMessage` and emits
   `stream-message`, but declares neither in rtc-sdk_en.d.ts. Verified by
   reading the shipped bundle, not by hoping. The two casts that need
   this live in the hook, named and explained; nothing else has to know. */

export const CHAT_VERSION = 1;

/* Agora's hard limit. Frames are built to fit UNDER it — see fitToBudget. */
export const FRAME_LIMIT_BYTES = 1024;
/* What's left for the message once the envelope has taken its share.
   Asserted by a test that builds a maximal frame and measures it. */
export const TEXT_BUDGET_BYTES = 700;
export const NAME_BUDGET_BYTES = 48;

/* How much scrollback a room keeps. Nothing persists anyway; this only
   stops a three-hour tour from growing an unbounded array. */
export const HISTORY_LIMIT = 200;

export type ChatRole = "guide" | "viewer";

export type ChatMessage = {
  /** Stable across sender and receivers — dedupes our own echo. */
  id: string;
  /** The participant, in the vocabulary the rest of the room uses. NOT
      the Agora uid: the people list is keyed by session_id, so a message
      maps onto a row with nothing to look up. */
  sessionId: string;
  name: string;
  role: ChatRole;
  text: string;
  /** Sender's clock, for display only — see appendMessage on ordering. */
  at: number;
};

export const byteLength = (s: string): number => new TextEncoder().encode(s).length;

/* Truncate to a BYTE budget without splitting a character in half. Never
   returns a lone surrogate: half an emoji renders as a replacement box on
   the receiving end, which reads as corruption rather than as a long
   message that got trimmed. */
export const fitToBudget = (raw: string, budget: number): string => {
  const s = String(raw || "");
  if (budget <= 0) return "";
  if (byteLength(s) <= budget) return s;

  let cut = s.length;
  while (cut > 0) {
    let candidate = s.slice(0, cut);
    const last = candidate.charCodeAt(candidate.length - 1);
    if (last >= 0xd800 && last <= 0xdbff) candidate = candidate.slice(0, -1);
    if (byteLength(candidate) <= budget) return candidate;
    cut -= 1;
  }
  return "";
};

/* What a person typed, made safe to send. Collapses the whitespace a
   paste brings with it and nothing else — "tidying up" what a buyer
   wrote is not this layer's business. */
export const sanitizeText = (raw: string): string =>
  fitToBudget(String(raw || "").replace(/\s+/g, " ").trim(), TEXT_BUDGET_BYTES);

export const sanitizeName = (raw: string): string =>
  fitToBudget(String(raw || "").trim(), NAME_BUDGET_BYTES) || "Guest";

/* Ids are made by the SENDER and travel with the message, so the local
   echo and the copy that reaches everyone else are the same message.
   Session id + clock + a counter: two messages from one person inside one
   millisecond is what a paste-then-send looks like. */
export const chatId = (sessionId: string, at: number, seq: number): string =>
  `${sessionId}:${at}:${seq}`;

/* ── the wire ──────────────────────────────────────────────────────────
   Short keys, because every byte spent on "sessionId" is a byte a buyer
   can't type. `t` identifies our frames: the channel is ours alone today,
   but anything else that ever shares it should be able to ignore what
   isn't addressed to it rather than choke on it. */
type Frame = {
  v: number; t: "c";
  i: string; s: string; n: string; r: ChatRole; x: string; a: number;
};

export const encodeChat = (m: ChatMessage): string => JSON.stringify({
  v: CHAT_VERSION,
  t: "c",
  i: m.id,
  s: m.sessionId,
  n: m.name,
  r: m.role,
  x: m.text,
  a: m.at,
} as Frame);

/* Anything unparseable, of another version, or not ours comes back null
   and the caller drops it. A malformed frame must never be able to put an
   empty bubble in a buyer's chat. */
export const decodeChat = (payload: unknown): ChatMessage | null => {
  let raw: string;
  if (typeof payload === "string") raw = payload;
  else if (payload instanceof ArrayBuffer) raw = new TextDecoder().decode(new Uint8Array(payload));
  else if (payload && ArrayBuffer.isView(payload as any)) {
    const v = payload as ArrayBufferView;
    raw = new TextDecoder().decode(new Uint8Array(v.buffer, v.byteOffset, v.byteLength));
  } else return null;

  let f: any;
  try { f = JSON.parse(raw); } catch { return null; }
  if (!f || f.t !== "c" || f.v !== CHAT_VERSION) return null;

  const body = String(f.x ?? "");
  const sessionId = String(f.s ?? "");
  if (!body || !sessionId) return null;

  return {
    id: String(f.i || chatId(sessionId, Number(f.a) || 0, 0)),
    sessionId,
    name: sanitizeName(String(f.n ?? "")),
    role: f.r === "guide" ? "guide" : "viewer",
    text: fitToBudget(body, TEXT_BUDGET_BYTES),
    at: Number(f.a) || 0,
  };
};

/* A frame the SDK would reject is worse than one that never left:
   sendStreamMessage THROWS, and a throw inside a submit handler leaves
   the sender believing a message was delivered. Checked every time. */
export const frameFits = (frame: string): boolean =>
  byteLength(frame) <= FRAME_LIMIT_BYTES;

/* ── the log ───────────────────────────────────────────────────────────
   ARRIVAL ORDER, not `at` order. Sorting by the sender's clock sounds
   more correct and is worse: phone clocks drift by minutes, and a buyer
   whose clock runs fast would have every message they send pinned to the
   bottom of everyone else's log. Arrival order is at least the same order
   for everyone. `at` is kept for the timestamp beside the bubble, which
   is allowed to be a little wrong. */
export const appendMessage = (
  list: ChatMessage[],
  m: ChatMessage,
  limit = HISTORY_LIMIT,
): ChatMessage[] => {
  if (list.some((x) => x.id === m.id)) return list;
  const next = [...list, m];
  return next.length > limit ? next.slice(next.length - limit) : next;
};

/* ── THE CONVERSATION THAT HAPPENED BEFORE YOU LOOKED ─────────────────
   Live chat rides Agora's data stream, so it exists only in the browser
   that received the frame. A buyer who refreshes — routine on a phone
   whenever a backgrounded tab reloads — came back mid-tour to an empty
   log while the whole conversation sat safely in the database.

   Ids are rebuilt with chatId(sessionId, at, 0), the same formula the
   live path uses for a frame that carries no id of its own, so a message
   restored from history and the same message arriving live dedupe against
   each other in appendMessage rather than appearing twice. That overlap
   is not hypothetical: a message sent while this request is in flight
   arrives by both routes.

   `role` is narrowed rather than trusted: it decides how a line is
   labelled, and "host" on a buyer's message would misattribute advice to
   the advisor. Anything unrecognised becomes "viewer", the least
   privileged label. */
export const historyToMessages = (
  rows: any[],
  opts: { hostName?: string } = {},
): ChatMessage[] => {
  /* The backend's vocabulary is host | lead | guest | agent; this file's
     is guide | viewer. Only the host is the guide — labelling a buyer's
     message as the advisor's would attribute their words to him. */
  const asRole = (r: any): ChatRole =>
    (String(r || "").toLowerCase() === "host" ? "guide" : "viewer");

  return (Array.isArray(rows) ? rows : [])
    .map((r: any) => {
      const sessionId = String(r?.session_id || "");
      const at = Number(r?.sent_at_ms) || 0;
      const text = sanitizeText(String(r?.text || ""));
      if (!text) return null;
      /* The host's own presence row carries no display_name — the backend
         knows them by token — so their restored lines would render
         nameless. The room already knows the advisor's name. */
      const name = sanitizeName(
        String(r?.display_name || "").trim()
          || (asRole(r?.role) === "guide" ? String(opts.hostName || "") : ""),
      );
      return { id: chatId(sessionId, at, 0), sessionId, name, role: asRole(r?.role), text, at };
    })
    .filter(Boolean)
    .sort((a: any, b: any) => a.at - b.at) as ChatMessage[];
};

/* 12-hour or 24-hour is the browser's business, not ours. */
export const chatTime = (at: number): string => {
  if (!at) return "";
  try {
    return new Date(at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
};
